import React from 'react';
import {useTranslation} from 'react-i18next';

import {Modal, showModal} from '@edna/components';
import {useMeasure} from '@edna/components/utils';

import {TItemId} from 'src/constants';
import {useBannerConfig} from 'src/containers/BannersConfig';
import {useAppearStyles} from 'src/hooks';

import {
  HorizontalNewsContainer,
  NewsBadgePanel,
  NewsBody,
  NewsImage,
  VerticalNewsContainer,
} from './NewsContent';
import * as S from './style';

const NewsAnimatedContainer = React.memo<{
  isOpen: boolean;
  children: React.ReactNode;
  noHighlight?: boolean;
  color?: S.TNewsBorderColor;
  className?: string;
}>(({isOpen, noHighlight, children, color, className}) => {
  const [contentRef, {height: contentHeight}] = useMeasure<HTMLDivElement>();

  const [isVisible, setIsVisible] = React.useState(isOpen);

  const styles = useAppearStyles({
    isVisible: isOpen,
    childrenHeight: contentHeight,
    hasOpacityDelay: true,
    easing: null,
    onClose: () => setIsVisible(false),
  });

  if (!isVisible) {
    return null;
  }

  return (
    <S.AnimatedDiv style={styles}>
      <div ref={contentRef}>
        <S.News className={className} color={color} noHighlight={noHighlight}>
          {children}
        </S.News>
      </div>
    </S.AnimatedDiv>
  );
});

const News = React.memo<{
  id: TItemId;
  children: ({onClose}: {onClose?: TAnyFunction}) => React.ReactNode;
  noHighlight?: boolean;
  color?: S.TNewsBorderColor;
}>(({id, children, noHighlight, color}) => {
  const {closeBanner, isBannerOpen} = useBannerConfig({id, category: 'NEWS'});

  return (
    <NewsAnimatedContainer color={color} isOpen={isBannerOpen} noHighlight={noHighlight}>
      {children({onClose: closeBanner})}
    </NewsAnimatedContainer>
  );
});

const NewsModal = React.memo<{
  title: string | React.ReactNode;
  children: React.ReactNode;
  image?: TSvgComponentType;
  onClose?: TEmptyFunction;
}>(({onClose, children, title, image}) => {
  const {t} = useTranslation();

  return (
    <Modal width="936px">
      <VerticalNewsContainer>
        <NewsBadgePanel />

        <HorizontalNewsContainer>
          <NewsBody title={title}>{children}</NewsBody>
          {image && <NewsImage image={image} />}
        </HorizontalNewsContainer>

        <S.Button appearance="primary" data-selector="Common:button.ok" onClick={onClose}>
          {t('Common:button.ok')}
        </S.Button>
      </VerticalNewsContainer>
    </Modal>
  );
});

const showNewsModal = ({
  children,
  image,
  title,
  onClose,
}: {
  title: string | React.ReactNode;
  children: React.ReactNode;
  image?: TSvgComponentType;
  onClose?: TEmptyFunction;
}) =>
  showModal((closeModal) => (
    <NewsModal
      onClose={() => {
        closeModal();
        onClose?.();
      }}
      image={image}
      title={title}
    >
      {children}
    </NewsModal>
  ));

const {News: NewsContainer} = S;

export {News, NewsContainer, NewsModal, showNewsModal, NewsAnimatedContainer};
